import * as React from 'react';

import { Badge } from '@apps/web/components/ui/badge';
import { Spinner } from '@apps/web/components/ui/spinner';
import {
    STATUS_INTENT_TOKENS,
    type StatusIntent,
    statusIntentBadgeClassName,
} from '@apps/web/components/ui/status_tokens';
import { cn } from '@apps/web/lib/utils';

export type StatusConfig = {
    readonly label: string;
    readonly showSpinner?: boolean;
    /**
     * Preferred: a semantic status intent. The dot and text use the base intent
     * tokens; statusIntentBadgeClassName supplies the soft pill treatment.
     */
    readonly intent?: StatusIntent;
    /**
     * Explicit palette overrides, for badges whose color is categorical rather
     * than a status intent (e.g. per-outreach-type hues). Ignored when `intent`
     * is set. Prefer `intent` for anything that represents a status.
     */
    readonly dotColor?: string;
    readonly backgroundColor?: string;
    readonly textColor?: string;
    readonly spinnerClassName?: string;
};

type StatusBadgeProps = {
    readonly status: StatusConfig;
    readonly className?: string;
} & React.ComponentProps<typeof Badge>;

/**
 * A badge that displays a colored dot indicator next to status text, similar to
 * Linear's status badges. Status pills should pass a semantic `intent`, which
 * resolves color from the canonical STATUS_INTENT_TOKENS map; categorical
 * badges may still pass explicit color classes.
 */
export function StatusBadge({ status, className, ...props }: StatusBadgeProps) {
    if (status.intent) {
        const tokens = STATUS_INTENT_TOKENS[status.intent];
        return (
            <Badge
                className={cn(
                    'inline-flex items-center gap-1.5',
                    statusIntentBadgeClassName(status.intent),
                    className,
                )}
                {...props}
            >
                {status.showSpinner ? (
                    <Spinner size="xs" className={tokens.text} />
                ) : (
                    <span
                        className={cn('h-2 w-2 rounded-full', tokens.dot)}
                        aria-hidden="true"
                    />
                )}
                <span>{status.label}</span>
            </Badge>
        );
    }

    const indicator = status.showSpinner ? (
        <Spinner
            size="xs"
            className={status.spinnerClassName ?? 'text-muted-foreground'}
        />
    ) : status.dotColor ? (
        <span
            className={cn('h-2 w-2 rounded-full', status.dotColor)}
            aria-hidden="true"
        />
    ) : null;

    return (
        <Badge
            className={cn(
                'inline-flex items-center gap-1.5 border-0',
                status.backgroundColor,
                status.textColor,
                className,
            )}
            {...props}
        >
            {indicator}
            <span>{status.label}</span>
        </Badge>
    );
}
